// src/pages/SevenAmLibraryEp1.jsx
// 7AM Library — Episodio 1: El error que se repite no es mala suerte
// Página pública, sin autenticación requerida.

import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Headphones,
  BookOpen,
  Lightbulb,
  HelpCircle,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import SevenAmSubscribe from '../components/SevenAmSubscribe';

// ─────────────────────────────────────────────────────────────────────────────
// REPRODUCTOR DE AUDIO
// ─────────────────────────────────────────────────────────────────────────────
function AudioPlayer({ src }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };
  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !muted;
    setMuted(!muted);
  };
  const handleTimeUpdate = () => { if (audioRef.current) setCurrentTime(audioRef.current.currentTime); };
  const handleLoadedMetadata = () => { if (audioRef.current) setDuration(audioRef.current.duration); };
  const handleEnded = () => setPlaying(false);
  const handleSeek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = ratio * duration;
    setCurrentTime(ratio * duration);
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    return `${m}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };
  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="w-full">
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        preload="metadata"
      />
      {/* Barra de progreso */}
      <div
        className="w-full h-1.5 bg-white/20 rounded-full mb-5 cursor-pointer relative overflow-hidden"
        onClick={handleSeek}
      >
        <div
          className="absolute inset-y-0 left-0 bg-amber-400 rounded-full transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>
      {/* Controles */}
      <div className="flex items-center gap-4">
        <button
          onClick={toggle}
          className="flex-shrink-0 w-11 h-11 rounded-full bg-amber-400 hover:bg-amber-300 flex items-center justify-center transition-colors shadow-lg"
          aria-label={playing ? 'Pausar' : 'Reproducir'}
        >
          {playing
            ? <Pause className="w-4 h-4 text-slate-900" strokeWidth={2.5} />
            : <Play className="w-4 h-4 text-slate-900 ml-0.5" strokeWidth={2.5} />
          }
        </button>
        <div className="flex items-center gap-1 text-xs font-mono tabular-nums text-white/60">
          <span className="text-white">{fmt(currentTime)}</span>
          <span>/</span>
          <span>{duration > 0 ? fmt(duration) : '~12:00'}</span>
        </div>
        <div className="flex-1" />
        <button
          onClick={toggleMute}
          className="text-white/50 hover:text-white transition-colors"
          aria-label={muted ? 'Activar sonido' : 'Silenciar'}
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PÁGINA PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
export default function SevenAmLibraryEp1() {
  return (
    <>
      <SEOHead
        title="Ep. 1 — El error que se repite no es mala suerte | 7AM Library"
        description="Por qué las crisis recurrentes de tu organización no son fallos de ejecución — son el resultado exacto de cómo está diseñada su estructura. Basado en Thinking in Systems, Donella Meadows."
      />

      <div className="min-h-screen" style={{ backgroundColor: '#F7F9FB' }}>
        <Navbar />

        {/* ── HEADER PORTADA EDITORIAL ── */}
        <section className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
            <Link
              to="/7am-library"
              className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6 transition-colors text-sm"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              7AM Library
            </Link>

            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                7AM Library · Episodio 1 de 10
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-4" />
              <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-3">
                El error que se repite no es mala suerte
              </h1>
              <p className="text-base sm:text-xl text-slate-500 font-light tracking-wide leading-relaxed">
                Por qué las crisis recurrentes de tu organización son el resultado
                exacto de cómo está diseñada su estructura — no de cómo trabaja tu equipo.
              </p>
            </div>
          </div>
        </section>

        {/* ── AUDIO PLAYER ── */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-12">
          <div
            className="rounded-2xl overflow-hidden shadow-xl"
            style={{ background: 'linear-gradient(135deg, #0c1a2e 0%, #162944 60%, #0e2240 100%)' }}
          >
            {/* Franja de acento superior — ámbar en lugar de cyan */}
            <div className="h-1 w-full bg-gradient-to-r from-amber-400 to-yellow-500" />

            <div className="px-3 sm:px-10 py-5 sm:py-10">
              {/* Metadatos */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-amber-300 text-xs font-semibold px-3 py-1.5 rounded-full">
                  <Headphones className="w-3.5 h-3.5" />
                  Episodio 1 · ~12 minutos
                </div>
                <div className="inline-flex items-center gap-2 text-white/40 text-xs">
                  <BookOpen className="w-3.5 h-3.5" />
                  <em>Thinking in Systems</em> · Donella Meadows
                </div>
              </div>

              {/* Frase */}
              <p className="text-white/70 text-sm sm:text-base leading-relaxed mb-6 italic max-w-2xl">
                &ldquo;Escucha por qué intervenir en el nivel del evento garantiza
                que el problema regrese.&rdquo;
              </p>

              {/* Player */}
              <AudioPlayer src="/assets/library/Audios/ep-1.m4a" />
            </div>
          </div>
        </section>

        {/* ── CONTENIDO EDITORIAL ── */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-5 sm:pb-12">
          <div className="bg-slate-50 rounded-xl sm:rounded-2xl border border-slate-200 px-4 sm:px-8 py-6 sm:py-10 space-y-8 sm:space-y-10">

            {/* 4. REFERENCIA INTELECTUAL */}
            <div className="flex items-start gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3">
              <BookOpen className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-0.5">
                  Conversación basada en
                </p>
                <p className="text-sm font-semibold text-slate-800 leading-snug">
                  Thinking in Systems
                </p>
                <p className="text-xs text-slate-500 mt-0.5">Donella Meadows</p>
              </div>
            </div>

            <div className="border-t border-slate-200" />

            {/* 6. IMAGEN — FLUJO CAUSAL */}
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                Imagen
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-3" />
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight mb-1">
                Estás interviniendo en el nivel equivocado
              </h2>
              <p className="text-sm text-slate-500 mb-4 leading-relaxed">
                Los tres niveles que separan una intervención superficial
                de una solución estructural real
              </p>

              <div className="flex justify-center">
                <img
                  src="/assets/library/Poster/ep-1.png"
                  alt="Infográfico de tres niveles apilados verticalmente. Nivel superior azul marino: Evento — la crisis que todos apagan. Nivel medio azul marino: Patrón — la repetición que nadie analiza. Nivel inferior teal: Estructura — donde está la solución real. Frase de cierre: No puedes resolver en el nivel del evento un problema diseñado en la estructura. 7AM Library by EduTech Pulse Lab."
                  className="w-full md:max-w-xl rounded-xl border border-slate-200"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            </div>

            <div className="border-t border-slate-200" />

            {/* 7. CONTEXTO */}
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                Contexto
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-3" />
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight mb-4">
                Contexto de esta conversación
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-3">
                Este episodio es un destilado ejecutivo de{' '}
                <em>Thinking in Systems</em> de Donella Meadows.
                No es un resumen del libro — es una conversación que extrae su idea
                más aplicable y la contrasta con situaciones reales de gestión y liderazgo.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-3">
                Este episodio explora por qué las organizaciones resuelven las mismas
                crisis una y otra vez sin que nada cambie de fondo.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                No es un problema de talento ni de compromiso. Es un problema de nivel
                de intervención: casi todas las decisiones ejecutivas operan en la
                superficie de un sistema que sigue generando el mismo resultado.
              </p>
            </div>

            {/* 8. POR QUÉ IMPORTA */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <h3 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2 tracking-[0.05em] uppercase">
                <Lightbulb className="w-4 h-4 text-amber-600" />
                Por qué importa
              </h3>
              <p className="text-base text-slate-600 leading-relaxed mb-3">
                Cada organización tiene su crisis trimestral. Puede ser el colapso
                logístico del cierre, el cuello de botella en ventas, el conflicto
                que reaparece entre áreas. La respuesta habitual es siempre la misma:
                esfuerzo extraordinario, solución temporal, celebración y olvido. Tres
                meses después, la misma escena.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                Lo que Meadows demuestra es que ese ciclo no es inevitable — es el
                resultado predecible de una estructura que nadie ha modificado. Mientras
                la organización siga interviniendo en el nivel del evento, la estructura
                seguirá produciendo el mismo patrón. El problema no es la gente. Es el
                diseño.
              </p>
            </div>

            {/* 9. IDEA CENTRAL */}
            <div className="border-l-4 border-amber-400 pl-5 py-1">
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                Idea central
              </p>
              <p className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight leading-snug mb-4">
                Una organización no puede resolver en el nivel del evento un problema
                que fue diseñado en el nivel de la estructura.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                Castigar al equipo no cambia el sistema.
                Prohibir el gasto no cambia los incentivos.
                Solo rediseñar la estructura rompe el ciclo.
              </p>
            </div>

            {/* 10. CORRECCIÓN EDITORIAL — solo metadata interna, no se muestra al lector */}

            <div className="border-t border-slate-200" />

            {/* 11. APLICACIÓN PRÁCTICA */}
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                Aplicación práctica
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-3" />
              <h3 className="text-lg sm:text-xl font-semibold text-slate-900 tracking-tight mb-4">
                Aplicación práctica
              </h3>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                Identifica una crisis que se repite en tu área — mismo síntoma, mismo
                momento, misma respuesta.
              </p>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                Hazte estas tres preguntas en orden:
              </p>
              <div className="border-l-4 border-amber-400 pl-5 space-y-3 mb-4">
                {[
                  '¿Qué evento estamos resolviendo esta vez?',
                  '¿Qué patrón muestran los últimos 4 ciclos?',
                  '¿Qué elemento del diseño — proceso, incentivo, política — está generando ese patrón?',
                ].map((q, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 text-xs font-bold text-amber-600 mt-0.5 w-4">
                      {i + 1}.
                    </span>
                    <p className="text-base text-slate-600 leading-snug">{q}</p>
                  </div>
                ))}
              </div>
              <p className="text-base text-slate-600 leading-relaxed mb-3">
                La tercera pregunta no la respondas solo. Llévala a tu jefe o a tu
                equipo con los datos de la pregunta 2. Un patrón documentado tiene
                más peso que una opinión.
              </p>
              <p className="text-sm text-slate-500 leading-relaxed">
                <strong className="text-slate-600">Consejo:</strong>{' '}
                No necesitas autoridad para cambiar la estructura. Necesitas evidencia
                para que quien sí la tiene quiera cambiarla.
              </p>
            </div>

            {/* 12. PREGUNTA DE REFLEXIÓN */}
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3 flex items-center gap-2">
                <HelpCircle className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                Pregunta de reflexión
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-4" />
              <blockquote className="border-l-4 border-slate-300 pl-5 text-base sm:text-lg text-slate-700 leading-relaxed font-light italic">
                ¿Qué crisis recurrente en tu organización estamos tratando trimestre
                tras trimestre como un fallo de ejecución — cuando en realidad es el
                resultado exacto de cómo está diseñada la estructura que la rodea?
              </blockquote>
            </div>

          </div>
        </section>

        <SevenAmSubscribe />

        {/* ── NAVEGACIÓN EPISODIOS ── */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-5 sm:pb-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Episodio anterior */}
            <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200 px-4 sm:px-6 py-5 sm:py-7">
              <p className="text-xs font-bold text-slate-400 tracking-[0.2em] uppercase mb-3">
                Episodio anterior
              </p>
              <div className="w-8 h-0.5 bg-slate-300 mb-4" />
              <p className="text-sm text-slate-600 font-semibold mb-1">
                Episodio 0 — El cerebro que decide sin avisarte
              </p>
              <p className="text-sm text-slate-400 mb-4">Daniel Kahneman</p>
              <Link
                to="/7am-library/episodio-0"
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Ir al episodio 0
              </Link>
            </div>

            {/* Episodio siguiente */}
            <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200 px-4 sm:px-6 py-5 sm:py-7">
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                Siguiente episodio
              </p>
              <div className="w-8 h-0.5 bg-amber-400 mb-4" />
              <p className="text-sm text-amber-600 font-semibold mb-1">
                Episodio 2 — Noise
              </p>
              <p className="text-sm text-slate-500 mb-4">Kahneman, Sibony, Sunstein</p>
              <Link
                to="/7am-library/episodio-2"
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                Ir al episodio 2
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

          </div>
        </section>

        {/* ── PIE DE PÁGINA ── */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-12 sm:pb-16">
          <div className="border-t border-slate-200 pt-6 sm:pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-0.5">
                7AM Library by EduTech Pulse Lab
              </p>
              <p className="text-sm text-slate-500">
                No resumimos libros. Destilamos ideas para pensar, decidir y actuar mejor.
              </p>
            </div>
            <Link
              to="/7am-library"
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-amber-600 transition-colors font-medium"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Ver todos los episodios
            </Link>
          </div>
        </section>

      </div>
    </>
  );
}
